<template>
  <div
    v-if="customIsVisible && !inHeadSection && props.code"
    :class="[model.backgroundColor, model.bottomMargin, model.verticalPadding]"
    class="inno-code"
  >
    <div class="flex flex-col mx-auto w-full max-w-screen-2xl px-5 md:px-12 block-content">
      <!-- Innovadis custom: use VcTypography for consistency-->
      <VcTypography v-if="title" :tag="model.heading" :anchor-id="title">
        {{ model.title }}
      </VcTypography>
      <div v-if="contentValue" v-html-safe="contentValue" class="inline-flex flex-col text-lg mt-4"></div>
      <div v-if="code" v-html-safe="code" class="inline-flex flex-col text-lg mt-4"></div>
      <div v-if="model.buttons?.length" class="my-4 flex space-x-4">
        <InnoButton
          v-for="(item, index) in model.buttons"
          :key="index"
          v-bind="getLinkAttr(item.link)"
          class="flex-1 lg:flex-none"
          color="primary"
          :target="'externalLink' in getLinkAttr(item.link) ? '_blank' : '_self'"
          :variant="model.buttons.length > 1 && index === 0 ? 'solid' : 'outline'"
          shape="pill"
          size="lg"
        >
          {{ item.label }}
        </InnoButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { getLinkAttr } from "@/core/utilities/common";
import InnoButton from "@/ui-kit/components/molecules/innovadis/inno-button.vue";

type ButtonType = {
  label: string;
  link: string;
};

interface IProps {
  heading?: string;
  title?: string;
  text?: string | { html: string };
  code: string;
  inHeadSection?: boolean; // when true, all is ignored and 'code' value needs to be added to head section
  buttons?: Array<ButtonType>;

  id: string;
  background?: string;
  model: any;
  settings: any;

  customIsVisible: boolean;

  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
  textAsLayover: false,
  imagePosition: "left",
  inHeadSection: false,
});

const contentValue = computed(() => {
  return typeof props.text === "object" ? props.text?.html : props.text;
});

onMounted(() => {
  if (props.inHeadSection && props.code) {
    const tpl = document.createElement("template");
    tpl.innerHTML = props.code.trim();

    Array.from(tpl.content.children).forEach((node, index) => {
      const tag = node.tagName?.toUpperCase();

      // Build unique tag ID for this script/style block
      const tagId = `${props.id}-${tag?.toLowerCase()}-${index}`;

      if (tag === "SCRIPT") {
        const existing = document.head.querySelector(`script[data-inno-head-id="${tagId}"]`);
        if (existing) return;

        const original = node as HTMLScriptElement;
        const script = document.createElement("script");

        Array.from(original.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value);
        });

        script.textContent = original.textContent || "";
        script.setAttribute("data-inno-head-id", tagId);

        document.head.appendChild(script);
      }

      if (tag === "STYLE") {
        const existing = document.head.querySelector(`style[data-inno-head-id="${tagId}"]`);
        if (existing) return;

        const style = node.cloneNode(true) as HTMLElement;
        style.setAttribute("data-inno-head-id", tagId);

        document.head.appendChild(style);
      }
    });
  }
});
</script>
